// Customer portal — Delivery tracking section.
//
// Renders an "active" delivery (anything not yet `delivered`) with:
//   - KOS reference pill (copyable via the Clipboard API)
//   - Carrier badge (DPD / APC / DHL / Royal Mail / Other)
//   - Status pill ("Scheduled" / "In Transit" / "Out for Delivery" /
//     "Delivered" / "Failed")
//   - ETA (relative "Tomorrow, 14:00" when within ±7 days, otherwise absolute)
//   - "Get tracking link" CTA that opens the carrier's public tracking page
//     when we know the URL pattern, else copies the tracking number
//   - A vertical timeline of `delivery.events`
//
// Source-of-truth (legacy):
//   /var/www/kitchenos/customer-standalone.html  ~line 788  (#page-delivery)
//   /var/www/kitchenos/customer-live.js          ~line 730  (renderDelivery)
//
// Backend
// -------
// Tracking events come from `project.deliveries[].events` (populated by the
// chrome via the `/api/customer/me` / project loader). This section does not
// fetch any custom endpoint — it triggers `ctx.refresh()` every 30s to pull
// the freshest project payload, which transparently picks up new events.
// Polling is paused when the tab is hidden and resumed (with an immediate
// refresh) when it becomes visible again.
//
// Carrier URL patterns (verified May 2026 — public-facing):
//   DPD UK   : https://track.dpd.co.uk/parcels/{trackingNumber}
//   APC      : https://apc-overnight.com/receiving-a-parcel/tracking-results/?cons={trackingNumber}
//   DHL      : https://www.dhl.com/gb-en/home/tracking/tracking-parcel.html?submit=1&tracking-id={trackingNumber}
//   Royal Mail: https://www.royalmail.com/track-your-item#/tracking-results/{trackingNumber}
// If the carrier is unknown or `trackingNumber` is missing, the CTA falls
// back to copying the reference / tracking number to the clipboard.

import type {
  CustomerDelivery,
  CustomerProject,
  CustomerSection,
  SectionCleanup,
  SectionContext,
} from "../types";
import { ensureLeaflet, type LeafletNS, type LeafletMap, type LeafletMarker } from "../shell/leaflet-loader";

import "./tracking.css";

// ── Constants ───────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 30_000;

const STATUS_LABELS: Record<CustomerDelivery["status"], string> = {
  scheduled: "Scheduled",
  in_transit: "In Transit",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  failed: "Failed",
};

const STATUS_PILL_CLASS: Record<CustomerDelivery["status"], string> = {
  scheduled: "cust-pill cust-pill-info",
  in_transit: "cust-pill cust-pill-info",
  out_for_delivery: "cust-pill cust-pill-pending",
  delivered: "cust-pill cust-pill-ok",
  failed: "cust-pill cust-pill-danger",
};

interface CarrierMeta {
  label: string;
  className: string;
  /** Build the public tracking URL or return null if not known. */
  url(trackingNumber: string): string | null;
}

const CARRIERS: Record<string, CarrierMeta> = {
  dpd: {
    label: "DPD",
    className: "trk-carrier--dpd",
    url: (n) => `https://track.dpd.co.uk/parcels/${encodeURIComponent(n)}`,
  },
  apc: {
    label: "APC Overnight",
    className: "trk-carrier--apc",
    url: (n) =>
      `https://apc-overnight.com/receiving-a-parcel/tracking-results/?cons=${encodeURIComponent(n)}`,
  },
  dhl: {
    label: "DHL",
    className: "trk-carrier--dhl",
    url: (n) =>
      `https://www.dhl.com/gb-en/home/tracking/tracking-parcel.html?submit=1&tracking-id=${encodeURIComponent(n)}`,
  },
  royal_mail: {
    label: "Royal Mail",
    className: "trk-carrier--royal-mail",
    url: (n) =>
      `https://www.royalmail.com/track-your-item#/tracking-results/${encodeURIComponent(n)}`,
  },
  // Fallback bucket — carrier is set but we don't know the URL pattern.
  other: {
    label: "Carrier",
    className: "trk-carrier--other",
    url: () => null,
  },
};

function carrierMeta(raw: string | null): CarrierMeta {
  if (!raw) return CARRIERS["other"]!;
  const key = raw.trim().toLowerCase().replace(/[\s-]/g, "_");
  if (key === "royal_mail" || key === "royalmail") return CARRIERS["royal_mail"]!;
  if (CARRIERS[key]) return CARRIERS[key]!;
  // Some servers send "dpd_local", "dhl_express" etc. — collapse the prefix.
  const head = key.split("_")[0] ?? "";
  return CARRIERS[head] ?? { ...CARRIERS["other"]!, label: raw };
}

// ── Formatters ──────────────────────────────────────────────────────────────

const ETA_ABS_FMT = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const TIME_FMT = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
});

const EVENT_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

function parseMs(s: string | null | undefined): number | null {
  if (!s) return null;
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** "Tomorrow, 14:00" / "Today, 09:30" / "Mon, 12 May, 10:00" / "—". */
function formatEta(iso: string | null): string {
  if (iso == null) return "—";
  const ms = parseMs(iso);
  if (ms == null) return "—";
  const date = new Date(ms);
  const dayDiff = Math.round((startOfDay(ms) - startOfDay(Date.now())) / 86_400_000);
  if (Math.abs(dayDiff) > 7) return ETA_ABS_FMT.format(date);
  const hasTime = !iso.endsWith("T00:00:00.000Z") && date.getHours() + date.getMinutes() > 0;
  const time = hasTime ? `, ${TIME_FMT.format(date)}` : "";
  if (dayDiff === 0) return `Today${time}`;
  if (dayDiff === 1) return `Tomorrow${time}`;
  if (dayDiff === -1) return `Yesterday${time}`;
  return `${RTF.format(dayDiff, "day")}${time}`;
}

function formatEventTime(iso: string | null | undefined): string {
  const ms = parseMs(iso ?? null);
  if (ms == null) return "—";
  return EVENT_FMT.format(new Date(ms));
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

// ── Clipboard ───────────────────────────────────────────────────────────────

async function copyText(text: string): Promise<boolean> {
  const trimmed = text.trim();
  if (!trimmed) return false;
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(trimmed);
      return true;
    }
  } catch {
    /* fall through to legacy */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = trimmed;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// ── Rendering ───────────────────────────────────────────────────────────────

function activeDeliveries(project: CustomerProject): CustomerDelivery[] {
  return project.deliveries.filter((d) => d.status !== "delivered");
}

function hasMap(d: CustomerDelivery): boolean {
  // Show the map when we have a live driver location for an active
  // delivery. Once the driver is "delivered" we hide the map (the
  // section also hides delivered deliveries entirely).
  return d.driverLocation != null && d.status !== "delivered" && d.status !== "failed";
}

function renderDeliveryHtml(d: CustomerDelivery): string {
  const carrier = carrierMeta(d.carrier);
  const statusClass = STATUS_PILL_CLASS[d.status];
  const statusLabel = STATUS_LABELS[d.status];

  const refHtml = d.kosReference
    ? `<button type="button" class="trk-ref" data-action="copy-ref"
              data-ref="${esc(d.kosReference)}" title="Copy KOS reference">
         <span class="trk-ref__text">${esc(d.kosReference)}</span>
         <svg class="trk-ref__icon" viewBox="0 0 24 24" aria-hidden="true">
           <rect x="9" y="9" width="11" height="11" rx="2"/>
           <path d="M5 15V5a2 2 0 0 1 2-2h10"/>
         </svg>
       </button>`
    : `<span class="trk-ref trk-ref--missing">No reference</span>`;

  const descHtml = d.description
    ? `<div class="trk-desc">${esc(d.description)}</div>`
    : "";

  const carrierHtml = d.carrier
    ? `<span class="trk-carrier ${carrier.className}">${esc(carrier.label)}</span>`
    : "";

  const trackBtn = d.trackingNumber
    ? `<button type="button" class="cust-btn cust-btn-secondary trk-link-btn"
              data-action="track-link"
              data-tracking="${esc(d.trackingNumber)}"
              data-carrier="${esc(d.carrier ?? "")}">
         ${carrier.url(d.trackingNumber) ? "Get tracking link" : "Copy tracking number"}
       </button>`
    : "";

  const events = (d.events ?? []).slice().sort((a, b) => {
    const am = parseMs(a.at) ?? 0;
    const bm = parseMs(b.at) ?? 0;
    return bm - am; // newest first
  });

  const timelineHtml =
    events.length === 0
      ? `<div class="trk-timeline-empty">No tracking events yet — we'll update this as your delivery progresses.</div>`
      : `<ol class="trk-timeline" role="list">
           ${events
             .map((e, idx) => {
               const isLatest = idx === 0;
               const dotCls = isLatest ? "trk-event__dot trk-event__dot--active" : "trk-event__dot";
               const statusLabel = e.status
                 ? esc(e.status.replace(/_/g, " "))
                 : "Update";
               return `<li class="trk-event${isLatest ? " trk-event--latest" : ""}">
                 <span class="${dotCls}" aria-hidden="true"></span>
                 <div class="trk-event__body">
                   <div class="trk-event__head">
                     <span class="trk-event__status">${statusLabel}</span>
                     <span class="trk-event__time">${esc(formatEventTime(e.at))}</span>
                   </div>
                   ${e.description ? `<div class="trk-event__desc">${esc(e.description)}</div>` : ""}
                 </div>
               </li>`;
             })
             .join("")}
         </ol>`;

  const mapHtml = hasMap(d)
    ? `<div class="trk-map-wrap">
         <div class="trk-map-head">
           <span class="trk-map-title">
             ${d.driverLocation?.driverName ? esc(d.driverLocation.driverName) + " is on the way" : "Driver on the way"}
           </span>
           <span class="trk-map-updated" data-role="updated"
                 data-updated-at="${esc(d.driverLocation?.updatedAt ?? "")}">
             ${esc(formatUpdatedAgo(d.driverLocation?.updatedAt ?? null))}
           </span>
         </div>
         <div class="trk-map" data-role="trk-map" data-delivery-id="${esc(d.id)}"></div>
       </div>`
    : "";

  return `
    <article class="trk-delivery" data-delivery-id="${esc(d.id)}">
      <div class="trk-delivery__head">
        <div class="trk-delivery__head-left">
          ${refHtml}
          ${carrierHtml}
        </div>
        <span class="${statusClass}">${esc(statusLabel)}</span>
      </div>
      ${descHtml}
      <div class="trk-meta">
        <div class="trk-meta__eta">
          <span class="trk-meta__label">ETA</span>
          <span class="trk-meta__value">${esc(formatEta(d.eta))}</span>
        </div>
        ${trackBtn}
      </div>
      ${mapHtml}
      <div class="trk-timeline-wrap">
        <h4 class="trk-timeline-title">Tracking history</h4>
        ${timelineHtml}
      </div>
    </article>
  `;
}

function formatUpdatedAgo(iso: string | null): string {
  if (!iso) return "";
  const ms = parseMs(iso);
  if (ms == null) return "";
  const ageS = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (ageS < 10) return "Live";
  if (ageS < 60) return `Updated ${ageS}s ago`;
  const ageM = Math.round(ageS / 60);
  if (ageM < 60) return `Updated ${ageM} min ago`;
  const ageH = Math.round(ageM / 60);
  return `Updated ${ageH} hr ago`;
}

function renderCardHtml(project: CustomerProject): string {
  const deliveries = activeDeliveries(project);
  if (deliveries.length === 0) {
    // shouldRender should prevent this branch from being hit at mount-time,
    // but `refresh()` can flip the project state mid-session — render an
    // empty state rather than a stale list.
    return `
      <section class="cust-card trk-card" aria-label="Delivery tracking">
        <h2 class="cust-card-title">Delivery tracking</h2>
        <div class="cust-empty">Your delivery has been completed.</div>
      </section>
    `;
  }
  return `
    <section class="cust-card trk-card" aria-label="Delivery tracking">
      <h2 class="cust-card-title">Delivery tracking</h2>
      <div class="trk-list">
        ${deliveries.map(renderDeliveryHtml).join("")}
      </div>
    </section>
  `;
}

// ── Section ─────────────────────────────────────────────────────────────────

const section: CustomerSection = {
  id: "tracking",
  label: "Delivery tracking",

  shouldRender(project: CustomerProject): boolean {
    return project.deliveries.some((d) => d.status !== "delivered");
  },

  mount(container: HTMLElement, ctx: SectionContext): SectionCleanup {
    const ac = new AbortController();
    let disposed = false;

    interface MapState {
      L: LeafletNS;
      map: LeafletMap;
      driverMarker: LeafletMarker;
      destMarker: LeafletMarker | null;
    }
    /** Map handles keyed by deliveryId so we can update markers on refresh. */
    const mapStates = new Map<string, MapState>();

    function disposeAllMaps(): void {
      for (const [, st] of mapStates) {
        try {
          st.driverMarker.remove();
          st.destMarker?.remove();
          st.map.remove();
        } catch {
          /* ignore */
        }
      }
      mapStates.clear();
    }

    async function mountMaps(): Promise<void> {
      const deliveries = activeDeliveries(ctx.project).filter(hasMap);
      if (deliveries.length === 0) return;

      let L: LeafletNS;
      try {
        L = await ensureLeaflet();
      } catch (err) {
        console.warn("[tracking] Leaflet failed to load — map disabled", err);
        return;
      }
      if (disposed) return;

      const driverIcon = L.divIcon({
        className: "trk-driver-pin",
        html: "<span class=\"trk-driver-pin__dot\"></span>",
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const destIcon = L.divIcon({
        className: "trk-dest-pin",
        html: "<span class=\"trk-dest-pin__pin\">🏠</span>",
        iconSize: [28, 28],
        iconAnchor: [14, 26],
      });

      for (const d of deliveries) {
        if (!d.driverLocation) continue;
        const el = container.querySelector<HTMLElement>(
          `[data-role='trk-map'][data-delivery-id='${cssEscape(d.id)}']`,
        );
        if (!el) continue;

        // Skip if we already mounted this delivery's map (refresh case).
        if (mapStates.has(d.id)) continue;

        const driverPoint: [number, number] = [d.driverLocation.lat, d.driverLocation.lng];
        const destPoint: [number, number] | null = d.destination
          ? [d.destination.lat, d.destination.lng]
          : null;

        const map = L.map(el, {
          zoomControl: true,
          scrollWheelZoom: false,
          attributionControl: true,
        });

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution:
            '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);

        const driverMarker = L.marker(driverPoint, {
          icon: driverIcon,
          title: d.driverLocation.driverName ?? "Driver",
        }).addTo(map);

        let destMarker: LeafletMarker | null = null;
        if (destPoint) {
          destMarker = L.marker(destPoint, { icon: destIcon, title: "Your address" }).addTo(map);
        }

        if (destPoint) {
          map.fitBounds(L.latLngBounds([driverPoint, destPoint]), {
            padding: [40, 40],
          });
        } else {
          map.setView(driverPoint, 13);
        }

        // Leaflet sometimes mis-measures inside flex containers; nudge it
        // once the next frame settles.
        requestAnimationFrame(() => map.invalidateSize());

        mapStates.set(d.id, { L, map, driverMarker, destMarker });
      }
    }

    function syncMaps(): void {
      const active = new Map(
        activeDeliveries(ctx.project)
          .filter(hasMap)
          .map((d) => [d.id, d] as const),
      );

      // Remove maps whose delivery is gone or no longer active.
      for (const [id, st] of mapStates) {
        if (!active.has(id)) {
          try {
            st.driverMarker.remove();
            st.destMarker?.remove();
            st.map.remove();
          } catch {
            /* ignore */
          }
          mapStates.delete(id);
        }
      }

      // Update markers for known maps.
      for (const [id, d] of active) {
        const st = mapStates.get(id);
        if (!st || !d.driverLocation) continue;
        const driverPoint: [number, number] = [d.driverLocation.lat, d.driverLocation.lng];
        st.driverMarker.setLatLng(driverPoint);
        // Keep view comfortable — fit bounds again if destination known.
        if (d.destination) {
          const destPoint: [number, number] = [d.destination.lat, d.destination.lng];
          if (!st.destMarker) {
            const destIcon = st.L.divIcon({
              className: "trk-dest-pin",
              html: "<span class=\"trk-dest-pin__pin\">🏠</span>",
              iconSize: [28, 28],
              iconAnchor: [14, 26],
            });
            st.destMarker = st.L.marker(destPoint, {
              icon: destIcon,
              title: "Your address",
            }).addTo(st.map);
          } else {
            st.destMarker.setLatLng(destPoint);
          }
          st.map.fitBounds(st.L.latLngBounds([driverPoint, destPoint]), {
            padding: [40, 40],
          });
        }
      }
    }

    function cssEscape(s: string): string {
      // CSS.escape isn't on every browser; fall back to a conservative
      // escape that handles our id format (alnum + hyphen + underscore).
      if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
        return CSS.escape(s);
      }
      return s.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
    }

    /** Set of delivery IDs currently displayed (used to detect when the
     * structure of the list has changed and a full re-render is needed). */
    let renderedDeliveryIds: string[] = [];

    function currentDeliveryIds(): string[] {
      return activeDeliveries(ctx.project).map((d) => d.id);
    }

    function fullRender(): void {
      // Tear down any existing maps before replacing the DOM — the
      // map mount targets are about to be detached.
      disposeAllMaps();
      container.innerHTML = renderCardHtml(ctx.project);
      renderedDeliveryIds = currentDeliveryIds();
      void mountMaps();
    }

    /** Refresh in place — update marker positions, ETA, timeline items,
     * "updated X ago" label — without remounting the map. */
    function smartRefresh(): void {
      // If the set of active deliveries changed, fall back to full
      // re-render — section structure is different now.
      const nowIds = currentDeliveryIds();
      const same =
        nowIds.length === renderedDeliveryIds.length &&
        nowIds.every((id, i) => id === renderedDeliveryIds[i]);
      if (!same) {
        fullRender();
        return;
      }

      // Update each delivery card in place.
      for (const d of activeDeliveries(ctx.project)) {
        const article = container.querySelector<HTMLElement>(
          `[data-delivery-id='${cssEscape(d.id)}']`,
        );
        if (!article) continue;

        // ETA
        const etaEl = article.querySelector<HTMLElement>(".trk-meta__value");
        if (etaEl) etaEl.textContent = formatEta(d.eta);

        // Status pill
        const pill = article.querySelector<HTMLElement>(".trk-delivery__head .cust-pill");
        if (pill) {
          pill.className = STATUS_PILL_CLASS[d.status];
          pill.textContent = STATUS_LABELS[d.status];
        }

        // "Updated X ago" label
        const updEl = article.querySelector<HTMLElement>("[data-role='updated']");
        if (updEl && d.driverLocation) {
          updEl.dataset.updatedAt = d.driverLocation.updatedAt;
          updEl.textContent = formatUpdatedAgo(d.driverLocation.updatedAt);
        }

        // Timeline — replace the events list contents only.
        const timelineWrap = article.querySelector<HTMLElement>(".trk-timeline-wrap");
        if (timelineWrap) {
          const events = (d.events ?? []).slice().sort((a, b) => {
            const am = parseMs(a.at) ?? 0;
            const bm = parseMs(b.at) ?? 0;
            return bm - am;
          });
          // Regenerate just the events portion (cheap; small DOM).
          const eventsHtml =
            events.length === 0
              ? `<div class="trk-timeline-empty">No tracking events yet — we'll update this as your delivery progresses.</div>`
              : `<ol class="trk-timeline" role="list">
                   ${events
                     .map((e, idx) => {
                       const isLatest = idx === 0;
                       const dotCls = isLatest ? "trk-event__dot trk-event__dot--active" : "trk-event__dot";
                       const statusLabel = e.status ? esc(e.status.replace(/_/g, " ")) : "Update";
                       return `<li class="trk-event${isLatest ? " trk-event--latest" : ""}">
                         <span class="${dotCls}" aria-hidden="true"></span>
                         <div class="trk-event__body">
                           <div class="trk-event__head">
                             <span class="trk-event__status">${statusLabel}</span>
                             <span class="trk-event__time">${esc(formatEventTime(e.at))}</span>
                           </div>
                           ${e.description ? `<div class="trk-event__desc">${esc(e.description)}</div>` : ""}
                         </div>
                       </li>`;
                     })
                     .join("")}
                 </ol>`;
          const title = timelineWrap.querySelector<HTMLElement>(".trk-timeline-title");
          timelineWrap.innerHTML =
            (title ? `<h4 class="trk-timeline-title">${esc(title.textContent ?? "Tracking history")}</h4>` : "") +
            eventsHtml;
        }
      }

      // Move existing markers / fit bounds without re-mounting tiles.
      syncMaps();
    }

    fullRender();

    // ── Click handling: copy ref / open tracking link ────────────────────
    function onClick(e: MouseEvent): void {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const copyBtn = target.closest<HTMLButtonElement>("[data-action='copy-ref']");
      if (copyBtn) {
        e.preventDefault();
        const ref = copyBtn.dataset.ref ?? "";
        void (async () => {
          const ok = await copyText(ref);
          ctx.showToast(ok ? `Copied ${ref}` : "Could not copy reference", ok ? "success" : "error");
        })();
        return;
      }

      const trackBtn = target.closest<HTMLButtonElement>("[data-action='track-link']");
      if (trackBtn) {
        e.preventDefault();
        const tracking = trackBtn.dataset.tracking ?? "";
        const carrierKey = trackBtn.dataset.carrier ?? "";
        if (!tracking) return;
        const url = carrierMeta(carrierKey).url(tracking);
        if (url) {
          window.open(url, "_blank", "noopener,noreferrer");
        } else {
          void (async () => {
            const ok = await copyText(tracking);
            ctx.showToast(
              ok
                ? `Copied tracking number ${tracking}`
                : "Could not copy tracking number",
              ok ? "success" : "error",
            );
          })();
        }
        return;
      }
    }
    container.addEventListener("click", onClick);

    // ── Polling: pull fresh project every 30s while visible ──────────────
    let pollHandle: ReturnType<typeof setInterval> | null = null;
    let refreshing = false;

    async function refreshOnce(): Promise<void> {
      if (refreshing || disposed) return;
      refreshing = true;
      try {
        await ctx.refresh();
        if (disposed || ac.signal.aborted) return;
        smartRefresh();
      } catch (err) {
        if (isAbortError(err) || ac.signal.aborted) return;
        // Soft-fail — next tick will retry. No toast (background refresh).
        console.warn("[tracking] refresh failed", err);
      } finally {
        refreshing = false;
      }
    }

    function startPolling(): void {
      if (pollHandle != null) return;
      pollHandle = setInterval(() => {
        if (document.visibilityState === "hidden") return;
        void refreshOnce();
      }, REFRESH_INTERVAL_MS);
    }
    function stopPolling(): void {
      if (pollHandle != null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") {
        stopPolling();
      } else {
        void refreshOnce();
        startPolling();
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    startPolling();

    // ── "Updated X ago" ticker — runs every 15s, mutates text only so it
    // doesn't disturb the map.
    const tickHandle = setInterval(() => {
      if (disposed) return;
      const labels = container.querySelectorAll<HTMLElement>("[data-role='updated']");
      labels.forEach((el) => {
        el.textContent = formatUpdatedAgo(el.dataset.updatedAt ?? null);
      });
    }, 15_000);

    return (): void => {
      disposed = true;
      ac.abort();
      stopPolling();
      clearInterval(tickHandle);
      disposeAllMaps();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      container.removeEventListener("click", onClick);
    };
  },
};

export default section;
